import { mergeAttributes, VueNodeViewRenderer } from "@tiptap/vue-3";
import EditorImage from "./EditorImage.vue";
import { Plugin, Transaction } from "prosemirror-state";
import api from "@/modules/api";
import { useMate } from "@/stores/mate";
import { EditorView } from "@tiptap/pm/view";
import { showLoadingToast, showNotify } from "vant";
import { Node as PmNode } from "prosemirror-model";
import { Node } from "@tiptap/core";
import i18n from "@/locales";

export interface EditorImageOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    editorImage: {
      /** 添加结果到编辑区 */
      setImage: (options: {
        src: string;
        alt?: string;
        title?: string;
      }) => ReturnType;
    };
  }
}

export default Node.create<EditorImageOptions>({
  name: "editorImage",
  group: "block",
  draggable: true,
  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },
  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
    };
  },
  parseHTML() {
    return [{ tag: "editor-image" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "editor-image",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    ];
  },
  addNodeView() {
    return VueNodeViewRenderer(EditorImage);
  },
  addCommands() {
    return {
      setImage:
        (options: EditorImageOptions) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: options,
            })
            .focus()
            .run();
        },
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            drop(view, event) {
              const hasFiles =
                event.dataTransfer &&
                event.dataTransfer.files &&
                event.dataTransfer.files.length;

              if (!hasFiles) {
                return;
              }

              const images = Array.from(event.dataTransfer.files).filter(
                (file) => /image/i.test(file.type)
              );

              if (images.length === 0) {
                return;
              }

              event.preventDefault();
              const coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });
              uploadMediaAndCreateNode(images, view).then((nodes) => {
                nodes.forEach((node) => {
                  const transaction = view.state.tr.insert(
                    coordinates!.pos,
                    node
                  );
                  view.dispatch(transaction);
                });
              });
            },
            paste(view, event) {
              const hasFiles =
                event.clipboardData &&
                event.clipboardData.files &&
                event.clipboardData.files.length;
              if (!hasFiles) {
                return;
              }
              const images = Array.from(event.clipboardData.files).filter(
                (file) => /image/i.test(file.type)
              );
              if (images.length === 0) {
                return;
              }
              event.preventDefault();
              uploadMediaAndCreateNode(images, view).then((nodes) => {
                nodes.forEach((node) => {
                  const transaction = (
                    view.state.tr as Transaction
                  ).replaceSelectionWith(node);
                  view.dispatch(transaction);
                });
              });
            },
          },
        },
      }),
    ];
  },
});

async function uploadMediaAndCreateNode(
  images: File[],
  view: EditorView
): Promise<PmNode[]> {
  const { schema } = view.state;
  const mate = useMate();
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.processingImage"),
  });
  const nodes: PmNode[] = [];
  for (const image of images) {
    try {
      const res = await api.uploadMedia(3, image, mate.currentMate!.id);
      const node = schema.nodes.editorImage.create({
        src: res.item.url,
      });
      nodes.push(node);
    } catch (e) {
      showNotify({ type: "danger", message: (e as Error).message });
    }
  }
  loadingToast.close();
  return nodes;
}
